import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Profile from "../../assets/images/couple-default.png";
import AuthApi from "../../api/authApi";
import DatePicker from "react-datepicker";
import { Form, Button, Row, Col, Table } from "react-bootstrap";
import moment from "moment-timezone";
import Helpers from "../../components/Helpers";
import Loader from "../../components/Loader";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import SelectImport from "react-select";
import { useTranslation } from "react-i18next";
import companyLogo from "../../assets/images/logo.png";
import AlertModal from "../../components/AlertModal";
import AlertDelete from "../../components/AlertDelete";
import OtherParishModal from "../../components/OtherParishModal";
import PhoneInput from "../../components/PhoneInput";
import CopyableInput from "../../components/CopyableInput";
import axios from "axios";
import { resolveRetreatImageUrl } from "../../utils/processRetreatImage";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useParishOptions } from "../../hooks/useParishOptions";
import { useEmailDeliverability } from "../../hooks/useEmailDeliverability";
import EmailDeliverabilityStatus from "../../components/EmailDeliverabilityStatus";
import { shouldBlockOnInvalid } from "../../utils/emailDeliverability";
import { emailFormatError } from "../../utils/emailFormat";
import { trimIfOnChangeField } from "../../utils/trimFormFields";
import { getDayName } from "../../utils/helpers";
import "../../assets/css/RegistrationRetreatModern.css";

const FormSelect = Form.Select as any;
const Select = SelectImport as any;

type RetreatContactPhone = { name: string; number: string };

const formatUsPhone = (raw: string): string => {
  const digits = (raw || "").replace(/\D/g, "");
  const d = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (d.length === 10) {
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return (raw || "").trim();
};

const getParishAddressLines = (parish?: any): string[] => {
  if (!parish) return [];
  const cityState = [parish.city, parish.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, parish.zip].filter(Boolean).join(" ").trim();
  return [parish.address, cityStateZip].filter(Boolean);
};

const normalizeExternalLink = (link?: string | null): string => {
  const trimmed = (link || "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Contact details are only ever taken from the retreat description; when the
// description has none, the Questions card is hidden instead of guessing.
const parseRetreatContactInfo = (
  rawDesc: string,
): { emails: string[]; phones: RetreatContactPhone[] } => {
  let emails: string[] = [];
  const phones: RetreatContactPhone[] = [];

  if (rawDesc) {
    const html = rawDesc
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const mailtoEmails = Array.from(tempDiv.querySelectorAll('a[href^="mailto:"]'))
      .map((el) =>
        (el.getAttribute("href") || "")
          .replace(/^mailto:/i, "")
          .split("?")[0]
          .trim(),
      )
      .filter(Boolean);

    let text = (tempDiv.textContent || tempDiv.innerText || "").replace(/\u00a0/g, " ");
    // Keep names from running into emails when HTML tags were the only separators.
    text = text.replace(/(@[a-zA-Z0-9.-]+\.[a-z]{2,24})([A-Z])/g, "$1\n$2");

    const emailMatches = text.match(
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,24}(?![a-z])/g,
    );
    emails = Array.from(new Set([...(mailtoEmails || []), ...(emailMatches || [])]));

    const phoneRegex = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
    const seenNumbers = new Set<string>();
    let match: RegExpExecArray | null;
    while ((match = phoneRegex.exec(text)) !== null) {
      const number = formatUsPhone(match[0]);
      if (!number || seenNumbers.has(number)) continue;
      seenNumbers.add(number);

      const before = text.slice(Math.max(0, match.index - 48), match.index);
      const lineBreak = Math.max(before.lastIndexOf("\n"), before.lastIndexOf("\r"));
      const prefix = before
        .slice(lineBreak + 1)
        .replace(/^[\s(]+/, "")
        .replace(/[\s(\-–:)]+$/g, "")
        .trim();

      let name = "";
      if (prefix && /^[A-Za-z][A-Za-z .'-]*$/.test(prefix)) {
        name = prefix.replace(/\s+/g, " ");
      } else {
        const prevLine = before
          .slice(0, lineBreak >= 0 ? lineBreak : 0)
          .split(/\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .pop();
        if (prevLine && /^[A-Za-z][A-Za-z .'-]*$/.test(prevLine)) {
          name = prevLine.replace(/\s+/g, " ");
        }
      }

      phones.push({ name, number });
    }
  }

  return { emails, phones };
};

export const RegistrationRetreat = () => {
  let authApi = new AuthApi();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isPreview = queryParams.get("preview") === "true";
  const previewUser = useAuthUser();
  const { parishList, setParishList, loadParishList } = useParishOptions({
    grouped: true,
    includeNoneOther: true,
    autoLoad: false,
  });
  const canLoadById =
    isPreview ||
    previewUser?.userRole === "ADMIN" ||
    previewUser?.userRole === "SUBADMIN";
  const [validated, setValidated] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedRetreat, setSelectedRetreat] = useState<any>("");
  const [data, setData] = useState<Record<string, any>>({
    hisFirstName: "",
    hisLastName: "",
    hisEmail: "",
    hisMobile: "",
    herFirstName: "",
    herLastName: "",
    herEmail: "",
    herMobile: "",
    city: "",
    state: "",
    zip: "",
    notes: "",
    imageUrl: "",
    parishId: null,
    address: "",
    allergies: "",
    anniversary_date: "",
    under_age_35: "NO",
    emergency_name1: "",
    emergency_relationship1: "",
    emergency_phone1: "",
    emergency_name2: "",
    emergency_relationship2: "",
    emergency_phone2: "",
    hear_us: [],
    referral_from_a_friend_or_family_member: "",
    attendeeType: "",
  });
  const [selectedParish, setSelectedParish] = useState<any[]>([]);
  const [LoadData, setLoadData] = useState<boolean>(false);
  const [incommingRetreats, setIncommingRetreats] = useState<any[]>([]);
  const [roles, setRoles] = useState<any>({});
  const [status, setStatus] = useState<any>("");
  const [acknowledgments, setAcknowledgments] = useState<Record<string, any>>({
    husband_acknowledgment: false,
    wife_acknowledgment: false,
  });
  const [acknowledgmentAlert, setAcknowledgmentAlert] = useState<boolean>(false);

  const [show, setShow] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [matched, setMatched] = useState<boolean>(false);
  const [validation, setValidation] = useState<string>("");
  const [emailSearch, setEmailSearch] = useState<string>("");
  const [otherParishModalOpen, setOtherParishModalOpen] = useState<boolean>(false);
  const [emailErrors, setEmailErrors] = useState<Record<string, any>>({
    hisEmail: "",
    herEmail: "",
  });
  const hisEmailStatus = useEmailDeliverability(data.hisEmail);
  const herEmailStatus = useEmailDeliverability(data.herEmail);

  const { id } = useParams();

  useEffect(() => {
    loadParishList();
    getAllIncommingRetreats();
  }, [id]);

  const { t, i18n } = useTranslation();

  // const [isTempUpload, setIsTempUpload] = useState(false);

  const getAllIncommingRetreats = async () => {
    setLoadData(true);
    if (id && canLoadById) {
      await authApi
        .getRetreatDetail({ id })
        .then(async (response) => {
          if (response?.success && response.data && response.data.length > 0) {
            let retreat = response.data[0];
            if (
              retreat.status !== "ACTIVE" &&
              previewUser?.userRole !== "ADMIN"
            ) {
              alert("The Retreat ID you entered does not exist.");
              navigate("/registration-retreat");
              setLoadData(false);
              return;
            }
            await authApi.getPerishList().then((r) => {
              let myparish = r.data.find((i) => i.id === retreat.parishId);
              if (myparish) {
                retreat.myparish = myparish;
              }
              if (retreat.schedule) {
                let parsedSchedule =
                  typeof retreat.schedule === "string"
                    ? JSON.parse(retreat.schedule)
                    : retreat.schedule;
                if (Array.isArray(parsedSchedule)) {
                  parsedSchedule.forEach((item) => {
                    item.dayName = getDayName(item.date, retreat.language);
                  });
                  retreat.schedule = parsedSchedule;
                }
              }
            });
            retreat.image1 = resolveRetreatImageUrl(retreat.image1);
            i18n.changeLanguage(retreat.language);
            setSelectedRetreat(retreat);
            setIncommingRetreats([retreat]);
            setStatus({
              directoryStatus: retreat.directoryStatus,
              programStatus: retreat.programStatus,
            });
            getRolesOfRetreats(id);
          } else if (isPreview || canLoadById) {
            alert("The Retreat ID you entered does not exist.");
            if (!isPreview) navigate("/registration-retreat");
          } else {
            alert("The Retreat ID you entered does not exist.");
            navigate("/registration-retreat");
          }
          setLoadData(false);
        })
        .catch((error) => {
          console.log(error.toString());
          setLoadData(false);
        });
      return;
    }
    await authApi
      .getAllIncommingRetreats()
      .then(async (response) => {
        await authApi
          .getPerishList()
          .then((r) => {
            response.data.forEach((event) => {
              let myparish = r.data.find((i) => i.id === event.parishId);
              if (myparish) {
                event.myparish = myparish;
              }
              event.schedule.forEach((item) => {
                item.dayName = getDayName(item.date, event.language);
              });
            });
          })
          .catch((error) => {
            toast.error(error.toString());
          });
        if (id) {
          getRolesOfRetreats(id);
          let findRetreat = response.data.find((item) => item.id == id);
          setIncommingRetreats(response.data);
          if (findRetreat) {
            i18n.changeLanguage(findRetreat.language);
            if (findRetreat?.parishLink) {
              findRetreat.parishLink = findRetreat.parishLink.trim();

              if (!findRetreat.parishLink.startsWith("http")) {
                findRetreat.parishLink = `https://${findRetreat.parishLink}`;
              }
            }
            setSelectedRetreat(findRetreat);
            setStatus({
              directoryStatus: findRetreat.directoryStatus,
              programStatus: findRetreat.programStatus,
            });
          } else {
            alert("The Retreat ID you entered does not exist.");
            navigate("/registration-retreat");
          }
        } else {
          setIncommingRetreats(response.data);
          if (response && response.data[0] && response.data[0].id) {
            // getRolesOfRetreats(response.data[0].id);
            navigate(`/registration-retreat/${response.data[0].id}`);
            i18n.changeLanguage(response.data[0].language);
          }
          setSelectedRetreat(response.data[0]);
          setStatus({
            directoryStatus: response.data[0].directoryStatus,
            programStatus: response.data[0].programStatus,
          });
        }
        setLoadData(false);
      })
      .catch((error) => {
        toast.error(error.toString());
        setLoadData(false);
      });
  };

  const getRolesOfRetreats = async (id: any) => {
    await authApi.getRolesOfRetreats({ id: id }).then(async (response: any) => {
      setRoles(response.data);
    });
  };

  const handleAgeChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    let o = { ...data };
    o.under_age_35 = checked ? "YES" : "NO";
    setData(o);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    // Safe parsing of hear_us
    let hearUsArray: any[];
    if (Array.isArray(data.hear_us)) {
      hearUsArray = data.hear_us;
    } else if (typeof data.hear_us === "string" && data.hear_us.trim() !== "") {
      try {
        hearUsArray = JSON.parse(data.hear_us);
      } catch (error) {
        console.error("Error parsing hear_us:", error);
        hearUsArray = [];
      }
    } else {
      hearUsArray = [];
    }

    let updatedOptions: any[];
    if (checked) {
      updatedOptions = [...hearUsArray, name];
    } else {
      updatedOptions = hearUsArray.filter((option: any) => option !== name);
    }

    setData({ ...data, hear_us: updatedOptions });
  };

  const handleDatePicker = (e: any) => {
    let o = { ...data };
    o.anniversary_date = e;
    setData(o);
  };

  const handleChanges = (e: any) => {
    let { name, value } = e.target;
    if (typeof value === "string") value = trimIfOnChangeField(name, value);
    let d = { ...data };
    if (name === "imageUrl") {
      const file = e.target.files[0];
      if (file) {
        const fileSizeInMB = file.size / (1024 * 1024); // Convert size from bytes to MB
        if (fileSizeInMB > 3) {
          alert("File size should be less than 3 MB");
          return;
        } else {
          d[name] = file;
          const successMessage =
            name === "imageUrl"
              ? "The profile image has been successfully added."
              : "";
          toast.success(successMessage);
        }
      }
    } else {
      d[name] = value;
    }

    setData(d);
    if (name === "hisEmail" || name === "herEmail") {
      setEmailErrors((prev) => ({
        ...prev,
        [name]: emailFormatError(value),
      }));
    }
  };

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleChanges(e); // keep your original logic

    if (value.length > 2) {
      try {
        const res = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${value}`,
        );
        setSuggestions(res.data);
      } catch (error) {
        console.error("Error fetching address suggestions:", error);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSelect = (address: any) => {
    handleChanges({
      target: { name: "address", value: address.display_name },
    });
    setSuggestions([]);
  };

  const handleSelectParish = (val: any) => {
    setSelectedParish(val);
    let d = { ...data };
    if (val.value === "OTHER") {
      setOtherParishModalOpen(true);
      d.parishId = selectedRetreat.parishId;
    } else {
      d.parishId = val.value;
    }
    setData(d);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    e.preventDefault();
    e.stopPropagation();

    if (isPreview) {
      toast.info("Preview mode — registration is not submitted.");
      return;
    }

    if (!data.attendeeType) {
      toast.error(t("ret_team_selection"));
      setValidated(true);
      return;
    }
    if (
      !(
        data.hisFirstName ||
        data.hisLastName ||
        data.herFirstName ||
        data.herLastName
      )
    ) {
      toast.error(t("add_one_name"));
      setValidated(true);
      return;
    }
    if (!data.hisEmail && !data.herEmail) {
      toast.error(t("least_email_id"));
      setValidated(true);
      return;
    }
    if (shouldBlockOnInvalid(hisEmailStatus, herEmailStatus)) {
      toast.error(t("email_domain_invalid_submit"));
      setValidated(true);
      return;
    }
    console.log("data.parishId---->", data.parishId);

    if (!data.parishId) {
      toast.error(t("parish_required"));
      setValidated(true);
      return;
    }

    if (!data.anniversary_date) {
      toast.error(t("anniversary_date_required"));
      setValidated(true);
      return;
    }

    if (!data.allergies || data.allergies.trim() === "") {
      toast.error(t("dietary_allergies_required"));
      setValidated(true);
      return;
    }

    if (form.checkValidity()) {
      let data1 = new FormData();
      data1.append("file", data.imageUrl);
      let stateRef = { ...data };
      if (stateRef.parishId === "" || stateRef.parishId === null) {
        stateRef.parishId = selectedRetreat.parishId;
      }
      delete stateRef.imageUrl;
      stateRef.retreatId = selectedRetreat.id;
      data1.append("fromData", JSON.stringify(stateRef));
      const hasEmergencyContact1 =
        data.emergency_name1 &&
        data.emergency_phone1 &&
        data.emergency_relationship1;

      const hasEmergencyContact2 =
        data.emergency_name2 &&
        data.emergency_phone2 &&
        data.emergency_relationship2;

      if (!hasEmergencyContact1 && !hasEmergencyContact2) {
        toast.error(
          "Please provide at least one emergency contact with full details.",
        );
        return;
      }

      if (
        acknowledgments.husband_acknowledgment &&
        acknowledgments.wife_acknowledgment
      ) {
        setTimeout(() => {
          const element = document.getElementById("section-1");
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "nearest",
            });
          }
        }, 500);
        authApi
          .registerCoupleForRetrteat(data1)
          .then(async (res) => {
            if (res.success) {
              toast.success(t("couple_register_retreat_msg"));
              clearfield();
              setData({
                id: "",
                hisFirstName: "",
                hisLastName: "",
                hisEmail: "",
                hisMobile: "",
                herFirstName: "",
                herLastName: "",
                herEmail: "",
                herMobile: "",
                city: "",
                state: "",
                zip: "",
                notes: "",
                imageUrl: "",
                address: "",
                allergies: "",
                anniversary_date: "",
                under_age_35: "NO",
                emergency_name1: "",
                emergency_relationship1: "",
                emergency_phone1: "",
                emergency_name2: "",
                emergency_relationship2: "",
                emergency_phone2: "",
                hear_us: [],
                referral_from_a_friend_or_family_member: "",
                attendeeType: "",
              });
              if (res.checkInMainCouple === true) {
                navigate("/thankyou", {
                  state: {
                    value: "Old Register Couple",
                    hisEmail: res.data.hisEmail,
                    herEmail: res.data.herEmail,
                    data: selectedRetreat,
                  },
                });
              } else {
                navigate("/thankyou", {
                  state: {
                    value: "New Register Couple",
                    hisEmail: res.data.hisEmail,
                    herEmail: res.data.herEmail,
                    data: selectedRetreat,
                  },
                });
              }
            } else {
              if (res.msg == "Please add at least one name.") {
                toast.error(t("add_one_name"));
              } else if (res.msg == "Please add at least one email ID.") {
                toast.error(t("least_email_id"));
              } else if (
                res.msg == "Please select either a Retreatant or a Team Couple."
              ) {
                toast.error(t("ret_team_selection"));
              } else if (res.msg == "This Mobile is already used.") {
                toast.error(t("mobile_used"));
              } else if (
                res.msg ==
                "You are already registered for this Retreat with this Email id."
              ) {
                toast.error(t("already_registered"));
              } else {
                toast.error(res.msg);
              }
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        setAcknowledgmentAlert(true);
      }
    } else {
      setValidated(true);
    }

    if (emailErrors.hisEmail || emailErrors.herEmail) {
      toast.error("Please enter valid email addresses.");
      return;
    }
  };

  const clearfield = () => {
    setData({
      hisFirstName: "",
      hisLastName: "",
      hisEmail: "",
      hisMobile: "",
      herFirstName: "",
      herLastName: "",
      herEmail: "",
      herMobile: "",
      city: "",
      state: "",
      zip: "",
      notes: "",
      imageUrl: "",
      parishId: null,
      address: "",
      allergies: "",
      anniversary_date: "",
      under_age_35: "NO",
      emergency_name1: "",
      emergency_relationship1: "",
      emergency_phone1: "",
      emergency_name2: "",
      emergency_relationship2: "",
      emergency_phone2: "",
      hear_us: [],
      referral_from_a_friend_or_family_member: "",
      attendeeType: "",
    });
    setSelectedParish([]);
    setValidated(false);
    setEmailSearch("");
    setEmailErrors({
      hisEmail: "",
      herEmail: "",
    });
  };

  const handleRetreatChanges = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const selectedRetreat = incommingRetreats.find(
      (item: any) => item.title === selectedValue,
    );
    if (selectedRetreat) {
      i18n.changeLanguage(selectedRetreat.language);
      navigate(`/registration-retreat/${selectedRetreat.id}`);
      // getRolesOfRetreats(selectedRetreat.id);
      setSelectedRetreat(selectedRetreat);
      setStatus({
        directoryStatus: selectedRetreat.directoryStatus,
        programStatus: selectedRetreat.programStatus,
      });
    } else {
      console.log("Retreat not found for selected value:", selectedValue);
    }
  };

  const acknowledgmentCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    // Update acknowledgment state first
    const updatedState = {
      ...acknowledgments,
      [name]: checked,
    };

    // Disable alert if both checkboxes are checked
    if (
      updatedState.husband_acknowledgment &&
      updatedState.wife_acknowledgment
    ) {
      setAcknowledgmentAlert(false);
    }

    setAcknowledgments(updatedState);
  };

  const checkMailInDb = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimIfOnChangeField("emailSearch", e.target.value) as string;
    setEmailSearch(value);

    const trimmedValue = value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedValue) {
      setValidation("");
      return;
    }
    if (!emailRegex.test(trimmedValue)) {
      setValidation("");
      return;
    }
    await authApi
      .checkMailInDb({ email: trimmedValue, prevAttendeeType: data.attendeeType })
      .then((res) => {
        if (res.success) {
          let d = { ...data };
          setShow(true);
          d.hisFirstName = res.data.hisFirstName;
          d.hisLastName = res.data.hisLastName;
          d.hisEmail = res.data.hisEmail;
          d.hisMobile = res.data.hisMobile;
          d.herFirstName = res.data.herFirstName;
          d.herLastName = res.data.herLastName;
          d.herEmail = res.data.herEmail;
          d.herMobile = res.data.herMobile;
          d.city = res.data.city;
          d.state = res.data.state;
          d.zip = res.data.zip;
          d.notes = res.data.notes;
          d.parishId = res.data.parishId;
          d.imageUrl = res.data.imageUrl;
          d.address = res.data.address;
          d.allergies = res.data.allergies;
          d.anniversary_date = res.data.anniversary_date
            ? new Date(res.data.anniversary_date)
            : "";
          d.under_age_35 = res.data.under_age_35;
          d.emergency_name1 = res.data.emergency_name1;
          d.emergency_relationship1 = res.data.emergency_relationship1;
          d.emergency_phone1 = res.data.emergency_phone1;
          d.emergency_name2 = res.data.emergency_name2;
          d.emergency_relationship2 = res.data.emergency_relationship2;
          d.emergency_phone2 = res.data.emergency_phone2;
          d.hear_us = Array.isArray(res.data.hear_us)
            ? res.data.hear_us
            : res.data.hear_us &&
              typeof res.data.hear_us === "string" &&
              res.data.hear_us.trim() !== ""
              ? JSON.parse(res.data.hear_us)
              : [];
          d.referral_from_a_friend_or_family_member =
            res.data.referral_from_a_friend_or_family_member;
          d.attendeeType = res.prevAttendeeType;
          console.log("res.data.parishId--->", res.data.parishId);
          const selectedParishObj = parishList
            .flatMap((group) => group.options)
            .find((option) => option.value === res.data.parishId);
          setSelectedParish(selectedParishObj || null);
          setData(d);
          setValidation("");
        } else {
          setValidation(t("mail_already_exist_in_db"));
          setShow(false);
          setSelectedParish([]);
          setData({
            hisFirstName: "",
            hisLastName: "",
            hisEmail: "",
            hisMobile: "",
            herFirstName: "",
            herLastName: "",
            herEmail: "",
            herMobile: "",
            city: "",
            state: "",
            zip: "",
            notes: "",
            imageUrl: "",
            parishId: null,
            address: "",
            allergies: "",
            anniversary_date: "",
            under_age_35: "NO",
            emergency_name1: "",
            emergency_relationship1: "",
            emergency_phone1: "",
            emergency_name2: "",
            emergency_relationship2: "",
            emergency_phone2: "",
            hear_us: [],
            referral_from_a_friend_or_family_member: "",
            attendeeType: res.prevAttendeeType,
          });
        }
      })
      .catch((error) => {
        toast.error(error.toString());
      });
  };

  const handleClose = () => {
    setData({
      hisFirstName: "",
      hisLastName: "",
      hisEmail: "",
      hisMobile: "",
      herFirstName: "",
      herLastName: "",
      herEmail: "",
      herMobile: "",
      city: "",
      state: "",
      zip: "",
      notes: "",
      imageUrl: "",
      parishId: null,
      address: "",
      allergies: "",
      anniversary_date: "",
      under_age_35: "NO",
      emergency_name1: "",
      emergency_relationship1: "",
      emergency_phone1: "",
      emergency_name2: "",
      emergency_relationship2: "",
      emergency_phone2: "",
      hear_us: [],
      referral_from_a_friend_or_family_member: "",
      attendeeType: "",
    });
    setSelectedParish([]);
    setShow(false);
    setShowInfo(true);
    setMatched(false);
    setEmailSearch("");
    setEmailErrors({
      hisEmail: "",
      herEmail: "",
    });
  };

  const coupleMatched = () => {
    setShow(false);
    setShowInfo(true);
    setMatched(true);
  };

  const handleCloseInfoAlert = () => {
    setShowInfo(false);
  };

  const showPublicRetreatView = Boolean(
    selectedRetreat &&
      (isPreview || selectedRetreat.status === "ACTIVE"),
  );
  const showRegistrationForm = Boolean(
    selectedRetreat && (isPreview || selectedRetreat.status === "ACTIVE"),
  );

  return (
    <>
      <div className="container-scroller mainpannel rr-page-wrapper">
        <div className="container-fluid page-body-wrapper full-page-wrapper">
          {LoadData ? (
            <div className="content-wrapper align-items-center auth px-0">
              <div className="row w-100 mx-0">
                <div className="col-lg-10 mx-auto">
                  <div className="auth-form-light text-center main-register rr-glass-card py-5 px-4 px-sm-5">
                    <Loader />
                  </div>
                </div>
              </div>
            </div>
          ) : incommingRetreats && incommingRetreats.length ? (
            <div className="content-wrapper  align-items-center auth px-0">
              <div className="container  text-center">
                <div className="row justify-content-center">
                </div>
              </div>
              <div className="row w-100 mx-0">
                <div className="col-lg-11 col-xl-10 mx-auto">
                  {/* Top Header Bar */}
                  <div className="rr-top-header w-100 mb-3">
                    <div className="compLogo rr-logo-container p-0">
                      <img src={companyLogo} alt="logo" className="logo rr-top-logo" />
                    </div>
                    <Link
                      to="/create-ticket"
                      state={{ pageName: "registration-retreat" }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rr-need-help-link"
                    >
                      <span>{t("need_help")}</span>
                      <span className="rr-help-question-icon">?</span>
                    </Link>
                  </div>

                  {/* Main Upper Container Card */}
                  <div className="auth-form-light main-register rr-glass-card py-4 px-4 px-sm-5 mt-2 text-start">
                    {!LoadData ? (
                      selectedRetreat && showPublicRetreatView ? (
                        <div className="w-100">
                          {/* Top Hero Grid */}
                          <Row className="w-100 mx-0 align-items-center mb-4">
                            <Col lg={7} md={7} xs={12} className="pe-lg-4 text-start">
                              <div className="rr-upcoming-badge">
                                <i className="mdi mdi-calendar-outline me-1"></i>
                                {t("upcoming_retreat")}
                              </div>

                              <h1 className="rr-hero-parish-title">
                                {selectedRetreat.language === "spanish" && selectedRetreat?.myparish?.spanishParish
                                  ? selectedRetreat.myparish.spanishParish
                                  : selectedRetreat?.myparish?.parish || selectedRetreat.title || "Retreat"}
                                {selectedRetreat.language
                                  ? ` (${selectedRetreat.language === "spanish" ? "Español" : "English"})`
                                  : null}
                              </h1>

                              <h3 className="rr-hero-parish-subtitle">
                                {selectedRetreat.language === "spanish" && selectedRetreat?.myparish?.spanishParish
                                  ? selectedRetreat.myparish.spanishParish
                                  : selectedRetreat?.myparish?.parish || selectedRetreat.parishName || selectedRetreat.location || ""}
                              </h3>

                              {/* Date Row */}
                              <div className="rr-hero-meta-item">
                                <i className="mdi mdi-calendar-month-outline rr-hero-meta-icon"></i>
                                <div className="rr-hero-meta-text">
                                  {selectedRetreat.dateFrom && selectedRetreat.dateTo
                                    ? moment(selectedRetreat.dateFrom).isSame(selectedRetreat.dateTo, "day")
                                      ? moment(selectedRetreat.dateFrom).tz("America/New_York").format("MMM D, YYYY")
                                      : moment(selectedRetreat.dateFrom).isSame(selectedRetreat.dateTo, "month")
                                      ? `${moment(selectedRetreat.dateFrom).tz("America/New_York").format("MMM D")} – ${moment(selectedRetreat.dateTo).tz("America/New_York").format("D")}, ${moment(selectedRetreat.dateFrom).tz("America/New_York").format("YYYY")}`
                                      : `${moment(selectedRetreat.dateFrom).tz("America/New_York").format("MMM D")} – ${moment(selectedRetreat.dateTo).tz("America/New_York").format("MMM D")}, ${moment(selectedRetreat.dateFrom).tz("America/New_York").format("YYYY")}`
                                    : "Nov 27 – 29, 2026"}
                                </div>
                              </div>

                              {/* Location Row — the retreat's own address */}
                              <div className="rr-hero-meta-item">
                                <i className="mdi mdi-map-marker-outline rr-hero-meta-icon"></i>
                                <div className="rr-hero-meta-text">
                                  {selectedRetreat.location ? (
                                    selectedRetreat.location
                                      .split(/\r?\n/)
                                      .filter((line: string) => line.trim())
                                      .map((line: string, i: number) => (
                                        <div key={`loc-${i}`}>{line.trim()}</div>
                                      ))
                                  ) : (
                                    getParishAddressLines(selectedRetreat?.myparish).map(
                                      (line, i) => <div key={`loc-fb-${i}`}>{line}</div>,
                                    )
                                  )}
                                </div>
                              </div>
                            </Col>

                            <Col lg={5} md={5} xs={12} className="mt-4 mt-md-0">
                              <div className="rr-church-image-card">
                                <img
                                  src={resolveRetreatImageUrl(selectedRetreat.image1 || selectedRetreat.parishImageUrl) || Profile}
                                  alt={selectedRetreat?.myparish?.parish || "Retreat"}
                                />
                              </div>
                            </Col>
                          </Row>

                          {/* Retreat Theme */}
                          {selectedRetreat?.heading ? (
                            <div className="rr-theme-box w-100 my-3">
                              <div className="rr-theme-label">{t("retreat_theme")}</div>
                              <h2>
                                <i>{selectedRetreat.heading}</i>
                              </h2>
                            </div>
                          ) : null}

                          {/* Mission Banner */}
                          <div className="rr-mission-banner-card w-100">
                            <Row className="align-items-center">
                              <Col lg={2} md={3} xs={12} className="rr-mission-left mb-3 mb-md-0">
                                <i className="mdi mdi-heart-outline rr-mission-heart-icon"></i>
                                <div className="rr-mission-title-group">
                                  <p>{t("our_mission")}</p>
                                </div>
                              </Col>
                              <Col lg={10} md={11} xs={12} className="d-flex align-items-center">
                                <div className="rr-mission-divider d-none d-md-block"></div>
                                <div className="rr-mission-right-quote">
                                  {selectedRetreat.mission_statement ? (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: selectedRetreat.mission_statement,
                                      }}
                                    />
                                  ) : (
                                    `"The mission of the LOVESTRONG Marriage Ministry is to support, strengthen, and nurture the Christ-centered marital bond between a man and a woman utilizing an experiential-based weekend retreat program."`
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* Leadership Section */}
                          <div className="rr-leadership-section w-100">
                            <div className="rr-leadership-heading">
                              <div className="rr-leadership-heading-line"></div>
                              <span className="rr-leadership-heart-icon">♡</span>
                              <span>{t("retreat_leadership")}</span>
                              <span className="rr-leadership-heart-icon">♡</span>
                              <div className="rr-leadership-heading-line"></div>
                            </div>

                            <Row className="justify-content-center g-4">
                              {/* Lead Couple */}
                              <Col lg={4} md={4} sm={6} xs={12}>
                                <div className="rr-leadership-card">
                                  <img
                                    src={
                                      roles?.leadCouple?.imageUrl
                                        ? resolveRetreatImageUrl(roles.leadCouple.imageUrl)
                                        : Profile
                                    }
                                    alt="Lead Couple"
                                    className="rr-leader-photo coupleImage"
                                  />
                                  <div className="rr-leader-name">
                                    {roles?.leadCouple
                                      ? `${roles.leadCouple.hisFirstName || ""} ${
                                          roles.leadCouple.herFirstName ? "& " + roles.leadCouple.herFirstName : ""
                                        } ${roles.leadCouple.hisLastName || ""}`
                                      : "Paul & Acams Sam"}
                                  </div>
                                  <div className="rr-leader-role-badge rr-role-badge-lead">
                                    {t("lead_couple")}
                                  </div>
                                </div>
                              </Col>

                              {/* Co-Lead Couple */}
                              <Col lg={4} md={4} sm={6} xs={12} className="leadCoCouples">
                                <div className="rr-leadership-card">
                                  <img
                                    src={
                                      roles?.coLeadCouple?.imageUrl
                                        ? resolveRetreatImageUrl(roles.coLeadCouple.imageUrl)
                                        : Profile
                                    }
                                    alt="Co-Lead Couple"
                                    className="rr-leader-photo coupleImage"
                                  />
                                  <div className="rr-leader-name">
                                    {roles?.coLeadCouple
                                      ? `${roles.coLeadCouple.hisFirstName || ""} ${
                                          roles.coLeadCouple.herFirstName ? "& " + roles.coLeadCouple.herFirstName : ""
                                        } ${roles.coLeadCouple.hisLastName || ""}`
                                      : "Jesus & Olivia Martinez"}
                                  </div>
                                  <div className="rr-leader-role-badge rr-role-badge-colead">
                                    {t("co_lead_couple")}
                                  </div>
                                </div>
                              </Col>

                              {/* Mentor Couple */}
                              <Col lg={4} md={4} sm={6} xs={12} className="mentorCouples">
                                <div className="rr-leadership-card">
                                  <img
                                    src={
                                      roles?.mentor?.imageUrl
                                        ? resolveRetreatImageUrl(roles.mentor.imageUrl)
                                        : Profile
                                    }
                                    alt="Mentor Couple"
                                    className="rr-leader-photo coupleImage"
                                  />
                                  <div className="rr-leader-name">
                                    {roles?.mentor
                                      ? `${roles.mentor.hisFirstName || ""} ${
                                          roles.mentor.herFirstName ? "& " + roles.mentor.herFirstName : ""
                                        } ${roles.mentor.hisLastName || ""}`
                                      : "Joseph & Veronica Alva"}
                                  </div>
                                  <div className="rr-leader-role-badge rr-role-badge-mentor">
                                    {t("mentor_couple")}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* Retreat Schedule & Info Container */}
                          <div className="rr-schedule-container-card w-100">
                            <div className="rr-schedule-header-title">
                              <i className="mdi mdi-calendar-clock rr-schedule-header-icon"></i>
                              <span>{t("retreat_schedule")}</span>
                            </div>

                            <Row className="g-4 mb-4 align-items-stretch">
                              {/* Schedule Table */}
                              <Col lg={8} md={7} xs={12}>
                                <div className="table-responsive rounded-3 overflow-hidden">
                                  <Table className="rr-schedule-table-styled mb-0">
                                    <thead>
                                      <tr>
                                        <th>{t("day")}</th>
                                        <th>{t("date")}</th>
                                        <th>{t("start")}</th>
                                        <th>{t("end")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedRetreat?.schedule && selectedRetreat.schedule.length > 0 ? (
                                        selectedRetreat.schedule.map((item: any, i: number) => (
                                          <tr key={i}>
                                            <td>
                                              {getDayName(item.date, selectedRetreat.language)}
                                            </td>
                                            <td>
                                              {Helpers.convertInWordTimming(
                                                item.date,
                                                "MM-DD-YYYY",
                                                selectedRetreat.language === "spanish" ? "es" : "en",
                                              )}
                                            </td>
                                            <td>{item.from}</td>
                                            <td>{item.to}</td>
                                          </tr>
                                        ))
                                      ) : (
                                        <>
                                          <tr>
                                            <td>{selectedRetreat.language === "spanish" ? "Viernes" : "Friday"}</td>
                                            <td>{selectedRetreat.language === "spanish" ? "27 de Nov, 2026" : "November 27th, 2026"}</td>
                                            <td>05:00 pm</td>
                                            <td>07:00 pm</td>
                                          </tr>
                                          <tr>
                                            <td>{selectedRetreat.language === "spanish" ? "Sábado" : "Saturday"}</td>
                                            <td>{selectedRetreat.language === "spanish" ? "28 de Nov, 2026" : "November 28th, 2026"}</td>
                                            <td>05:00 pm</td>
                                            <td>07:00 pm</td>
                                          </tr>
                                          <tr>
                                            <td>{selectedRetreat.language === "spanish" ? "Domingo" : "Sunday"}</td>
                                            <td>{selectedRetreat.language === "spanish" ? "29 de Nov, 2026" : "November 29th, 2026"}</td>
                                            <td>05:00 pm</td>
                                            <td>07:00 pm</td>
                                          </tr>
                                        </>
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>

                              {/* All Retreat Couples Highlight Card */}
                              <Col lg={4} md={5} xs={12}>
                                <div className="rr-schedule-right-pink-card">
                                  <i className="mdi mdi-account-heart-outline rr-schedule-right-icon"></i>
                                  <h4 className="rr-schedule-right-title">{t("all_retreat_couples")}</h4>
                                  <p className="rr-schedule-right-text">
                                    {t("all_couples_expected_msg", {
                                      count:
                                        selectedRetreat?.schedule &&
                                        Array.isArray(selectedRetreat.schedule) &&
                                        selectedRetreat.schedule.length > 0
                                          ? selectedRetreat.schedule.length
                                          : selectedRetreat?.dateFrom && selectedRetreat?.dateTo
                                          ? Math.max(
                                              1,
                                              moment(selectedRetreat.dateTo).diff(
                                                moment(selectedRetreat.dateFrom),
                                                "days",
                                              ) + 1,
                                            )
                                          : 3,
                                    })}
                                  </p>
                                </div>
                              </Col>
                            </Row>

                            {/* Bottom Info Grid */}
                            {(() => {
                              const { emails, phones } = parseRetreatContactInfo(
                                selectedRetreat?.description || "",
                              );
                              const hasContactInfo =
                                emails.length > 0 || phones.length > 0;

                              return (
                                <Row className="g-4 align-items-stretch">
                                  <Col
                                    lg={hasContactInfo ? 8 : 12}
                                    md={hasContactInfo ? 7 : 12}
                                    xs={12}
                                  >
                                    <div className="rr-cost-info-box h-100">
                                      {selectedRetreat.description ? (
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: selectedRetreat.description,
                                          }}
                                        />
                                      ) : (
                                        "The cost is $175 per couple. A $75 deposit is due with the registration form. We are limited as to the number of couples we can accommodate, and availability is on a first come, first serve basis. The registration cost covers dinner on Friday, three meals on Saturday, and breakfast and lunch on Sunday. It will also cover all retreat materials. Childcare is not provided."
                                      )}
                                    </div>
                                  </Col>

                                  {hasContactInfo ? (
                                    <Col lg={4} md={5} xs={12}>
                                      <div className="rr-navy-questions-card h-100">
                                        <div className="rr-navy-questions-header">
                                          <i className="mdi mdi-email-outline rr-navy-mail-icon"></i>
                                          <h4 className="rr-navy-questions-title">
                                            {t("questions")}
                                          </h4>
                                        </div>
                                        <div className="rr-navy-contact-detail">
                                          {emails.map((e, idx) => (
                                            <div key={`em-${idx}`} className="rr-navy-contact-email">
                                              {e}
                                            </div>
                                          ))}
                                          {phones.length > 0 ? (
                                            <div className="rr-navy-contact-phones">
                                              {phones.map((p, idx) =>
                                                p.name ? (
                                                  <React.Fragment key={`ph-${idx}`}>
                                                    <span className="rr-navy-contact-name">{p.name}</span>
                                                    <span className="rr-navy-contact-number">- {p.number}</span>
                                                  </React.Fragment>
                                                ) : (
                                                  <span
                                                    key={`ph-${idx}`}
                                                    className="rr-navy-contact-number rr-navy-contact-number-only"
                                                  >
                                                    {p.number}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </Col>
                                  ) : null}
                                </Row>
                              );
                            })()}
                          </div>

                          {/* Hosting Parish */}
                          {(() => {
                            const parish = selectedRetreat?.myparish;
                            const parishName =
                              (selectedRetreat.language === "spanish" && parish?.spanishParish) ||
                              parish?.parish ||
                              selectedRetreat.parishName ||
                              "";
                            const parishImage = resolveRetreatImageUrl(
                              parish?.imageUrl || selectedRetreat.parishImageUrl,
                            );
                            const parishHref = normalizeExternalLink(
                              parish?.link || selectedRetreat.parishLink,
                            );
                            const addressLines = getParishAddressLines(parish);

                            if (!parishName && !parishImage && addressLines.length === 0) {
                              return null;
                            }

                            const logo = (
                              <img
                                src={parishImage}
                                alt={parishName || "Parish"}
                                className="rr-parish-logo"
                              />
                            );

                            return (
                              <div className="rr-parish-card w-100">
                                <div className="rr-parish-accent"></div>
                                <Row className="align-items-center g-4">
                                  {parishImage ? (
                                    <Col lg={3} md={4} xs={12}>
                                      <div className="rr-parish-logo-tile">
                                        {parishHref ? (
                                          <a
                                            href={parishHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {logo}
                                          </a>
                                        ) : (
                                          logo
                                        )}
                                      </div>
                                    </Col>
                                  ) : null}

                                  <Col lg={parishImage ? 9 : 12} md={parishImage ? 8 : 12} xs={12}>
                                    <div className="rr-parish-eyebrow">
                                      <i className="mdi mdi-church"></i>
                                      {t("hosting_parish")}
                                    </div>
                                    {parishName ? (
                                      <h3 className="rr-parish-name">{parishName}</h3>
                                    ) : null}
                                    {addressLines.length > 0 ? (
                                      <div className="rr-parish-address">
                                        <span className="rr-parish-address-badge">
                                          <i className="mdi mdi-map-marker-outline"></i>
                                        </span>
                                        <div className="rr-parish-address-lines">
                                          {addressLines.map((line, i) => (
                                            <div key={`pa-${i}`}>{line}</div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null}
                                    {parishHref ? (
                                      <div className="rr-parish-actions">
                                        <a
                                          className="rr-parish-btn rr-parish-btn-ghost"
                                          href={parishHref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <i className="mdi mdi-open-in-new"></i>
                                          {t("visit_parish_website")}
                                        </a>
                                      </div>
                                    ) : null}
                                  </Col>
                                </Row>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <Row>
                          <Col
                            lg={12}
                            md={12}
                            xs={12}
                            className="text-center mb-3"
                          >
                            <div className="retreat-Title-Regis">
                              <h3>
                                {selectedRetreat?.myparish?.parish}
                                {selectedRetreat.language
                                  ? ` (${selectedRetreat.language[0].toUpperCase()}${selectedRetreat.language.slice(
                                    1,
                                  )}) `
                                  : null}
                              </h3>
                              <h3>
                                {selectedRetreat.location
                                  ? `${selectedRetreat.location[0].toUpperCase()}${selectedRetreat.location.slice(
                                    1,
                                  )}`
                                  : ""}
                              </h3>
                              <p>
                                {selectedRetreat.dateFrom &&
                                  selectedRetreat.dateTo
                                  ? moment(selectedRetreat.dateFrom).isSame(
                                    selectedRetreat.dateTo,
                                    "day",
                                  )
                                    ? moment(selectedRetreat.dateFrom)
                                      .tz("America/New_York")
                                      .format("MMM D, YYYY")
                                    : moment(selectedRetreat.dateFrom).isSame(
                                      selectedRetreat.dateTo,
                                      "month",
                                    )
                                      ? `${moment(selectedRetreat.dateFrom)
                                        .tz("America/New_York")
                                        .format("MMM D")} - ${moment(
                                          selectedRetreat.dateTo,
                                        )
                                          .tz("America/New_York")
                                          .format("D")}, ${moment(
                                            selectedRetreat.dateFrom,
                                          )
                                            .tz("America/New_York")
                                            .format("YYYY")}`
                                      : `${moment(selectedRetreat.dateFrom)
                                        .tz("America/New_York")
                                        .format("MMM D")} - ${moment(
                                          selectedRetreat.dateTo,
                                        )
                                          .tz("America/New_York")
                                          .format("MMM D")}, ${moment(
                                            selectedRetreat.dateFrom,
                                          )
                                            .tz("America/New_York")
                                            .format("YYYY")}`
                                  : null}
                              </p>
                            </div>
                          </Col>
                          <Col lg={12} md={12} xs={12} className="mb-3">
                            <div className="message-container">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: selectedRetreat.inactiveReason,
                                }}
                              />
                            </div>
                            {selectedRetreat &&
                              selectedRetreat.language === "english" ? (
                              <Link to={`/register-waitList/${id}`}>
                                <h2 className="mt-3">JOIN THE WAITLIST!</h2>
                              </Link>
                            ) : (
                              <Link to={`/register-waitList/${id}`}>
                                <h2 className="mt-3">
                                  ¡ÚNETE A LA LISTA DE ESPERA!
                                </h2>
                              </Link>
                            )}
                          </Col>
                        </Row>
                      )
                    ) : (
                      <Loader />
                    )}
                  </div>
                </div>
              </div>
              {showRegistrationForm ? (
                <Row className="w-100 mx-0 justify-content-center mt-5">
                  <Col lg={12} md={12} xs={12} className="text-center">
                    <h3 className="RegisTitle">{t("registration_form")}</h3>
                    {/* <h4 className="retreatParish mt-3">
                    {selectedRetreat &&
                    selectedRetreat.myparish &&
                    selectedRetreat.myparish.parish
                      ? selectedRetreat.myparish.parish
                      : ""}
                  </h4>
                  <h5 className="parishAddress mt-3">
                    {selectedRetreat &&
                    selectedRetreat.myparish &&
                    selectedRetreat.myparish.address
                      ? selectedRetreat.myparish.address
                      : null}
                  </h5>
                  <h5 className="parishAddress mt-3">
                    {`${
                      selectedRetreat &&
                      selectedRetreat.myparish &&
                      selectedRetreat.myparish.city
                        ? selectedRetreat.myparish.city
                        : ""
                    }` +
                      `${
                        selectedRetreat &&
                        selectedRetreat.state &&
                        selectedRetreat.myparish.state
                          ? ", " + selectedRetreat.myparish.state
                          : ""
                      }` +
                      `${
                        selectedRetreat &&
                        selectedRetreat.zip &&
                        selectedRetreat.myparish.zip
                          ? " " + selectedRetreat.myparish.zip
                          : ""
                      }`}
                  </h5>

                  <h5 className="retreatParish mt-3">
                    {`${
                      roles && roles.leadCouple && roles.leadCouple.hisFirstName
                        ? roles.leadCouple.hisFirstName
                        : ""
                    }` +
                      `${
                        roles &&
                        roles.leadCouple &&
                        roles.leadCouple.hisLastName
                          ? roles.leadCouple.hisLastName
                          : ""
                      }` +
                      " " +
                      `${
                        roles && roles.leadCouple && roles.leadCouple.hisMobile
                          ? "(" + roles.leadCouple.hisMobile + ")"
                          : ""
                      }`}
                  </h5> */}
                  </Col>
                  <Col
                    lg={10}
                    md={10}
                    xs={10}
                    className="text-end requiredFieldMsg"
                  >
                    <span className="highlightedText">
                      {t("highlighted_text")}
                    </span>
                    <Link
                      to="/create-ticket"
                      state={{ pageName: "registration-retreat" }}
                    >
                      <div className="helpDiv">
                        <span className="helpMdi">{t("help")}</span>
                        <i
                          className="mdi mdi-help-circle"
                          style={{ fontSize: "22px" }}
                        ></i>
                      </div>
                    </Link>
                  </Col>
                </Row>
              ) : null}
              {showRegistrationForm ? (
                <div className="row w-100 mx-0">
                  <div className="col-lg-10 mx-auto">
                    <div className="auth-form-light text-left main-register-check py-5 px-4 px-sm-5 rr-glass-card">
                      {/* <h4 className="text-danger mb-5">
                      Please enter at least one Name and Email for registration.
                    </h4> */}

                      <Form
                        onSubmit={handleSubmit}
                        noValidate
                        validated={validated}
                        className="forms-sample formStyle"
                      >
                        <div className="rr-section-card mb-4">
                          <Row>
                            <Col lg={6} md={6} xs={12}>
                              <div>
                                <label
                                  htmlFor="exampleInputEmail1"
                                  className={
                                    !data?.attendeeType
                                      ? "highlightField1 mt-2 bold"
                                      : "bold mt-2"
                                  }
                                >
                                  {t("retreat_team")}
                                  <span className="text-danger">*</span>
                                </label>
                                <Form.Group className="form-group d-flex gap-3 mt-2">
                                  <div className="rr-radio-card">
                                    <Form.Check
                                      type="radio"
                                      id="retreatRad"
                                      name="attendeeType"
                                      value="Retreatant"
                                      label={t("retreatant")}
                                      className="space_lef1 mb-0"
                                      checked={data.attendeeType === "Retreatant"}
                                      onChange={handleChanges}
                                    />
                                  </div>
                                  <div className="rr-radio-card">
                                    <Form.Check
                                      type="radio"
                                      id="coupleRad"
                                      name="attendeeType"
                                      value="Team"
                                      label={t("team")}
                                      className="space_lef1 mb-0"
                                      checked={data.attendeeType === "Team"}
                                      onChange={handleChanges}
                                    />
                                  </div>
                                </Form.Group>
                              </div>
                              <div className="mt-3">
                                <label
                                  className="bold"
                                  htmlFor="exampleInputEmail1"
                                >
                                  {t("check_year")}
                                </label>
                                <div></div>
                                <label className="form-check-label mt-2 rr-checkbox-item">
                                  <input
                                    type="checkbox"
                                    className="form-check-input ageLabel m-0"
                                    name="under_age_35"
                                    checked={
                                      data.under_age_35 === "YES" ? true : false
                                    }
                                    onChange={handleAgeChanges}
                                  />
                                  {t("yes")}
                                  <i className="input-helper"></i>
                                </label>
                              </div>
                            </Col>

                            <Col lg={6} md={6} xs={12}>
                              <Form.Group className="form-group">
                                <Form.Label className="bold searchMailField">
                                  {t("search_mail_field")}
                                </Form.Label>
                                <CopyableInput
                                  size="sm"
                                  type="text"
                                  name="emailSearch"
                                  id="emailSearch"
                                  placeholder={`${t("search_mail_placeholder")}`}
                                  value={emailSearch}
                                  onChange={(e) => checkMailInDb(e)}
                                  isInvalid={validation !== ""} // Mark invalid when validation message exists
                                />
                                {validation && (
                                  <Form.Control.Feedback type="invalid">
                                    {validation}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>

                        <Row>
                          <Col lg={6} md={6} xs={12}>
                            <div className="rr-gender-card rr-his-card" id="his-info">
                              <span className="rr-gender-badge-his">
                                <i className="mdi mdi-account-tie"></i> {t("his_info")}
                              </span>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.hisFirstName
                                        ? "his-info"
                                        : "highlightField1 mt-2 his-info"
                                    }
                                  >
                                    {t("his_first_name")}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Control
                                    // required
                                    size="sm"
                                    type="text"
                                    name="hisFirstName"
                                    id="hisFirstName"
                                    placeholder={`${t("his_first_name")}`}
                                    value={data.hisFirstName}
                                    className="form-control his-info-border"
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    FirstName is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.hisLastName
                                        ? null
                                        : "highlightField1"
                                    }
                                  >
                                    {" "}
                                    {t("his_last_name")}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Control
                                    // required
                                    size="sm"
                                    type="text"
                                    name="hisLastName"
                                    id="hisLastName"
                                    placeholder={`${t("his_last_name")}`}
                                    value={data.hisLastName}
                                    className="form-control"
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    LastName is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group col">
                                  <Form.Label
                                    className={
                                      data.hisEmail ? null : "highlightField1"
                                    }
                                  >
                                    {t("his_email")}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <CopyableInput
                                    // required
                                    size="sm"
                                    type="text"
                                    name="hisEmail"
                                    id="hisEmail"
                                    placeholder={`${t("his_email")}`}
                                    value={data.hisEmail}
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  {emailErrors.hisEmail && (
                                    <span
                                      className="text-danger"
                                      style={{ fontSize: "12px" }}
                                    >
                                      {emailErrors.hisEmail}
                                    </span>
                                  )}
                                  <EmailDeliverabilityStatus status={hisEmailStatus} />
                                  <Form.Control.Feedback type="invalid">
                                    Email is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group col">
                                  <Form.Label
                                    className={
                                      data.hisMobile ? null : "highlightField1"
                                    }
                                  >
                                    {t("his_mobile")}
                                  </Form.Label>
                                  <PhoneInput
                                    name="hisMobile"
                                    value={data.hisMobile}
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Mobile is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </div>
                          </Col>
                          <Col lg={6} md={6} xs={12}>
                            <div className="rr-gender-card rr-her-card" id="her-info">
                              <span className="rr-gender-badge-her">
                                <i className="mdi mdi-account-heart"></i> {t("her_info")}
                              </span>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.herFirstName
                                        ? "her-info"
                                        : "highlightField1 mt-2 her-info"
                                    }
                                  >
                                    {t("her_first_name")}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Control
                                    // required
                                    size="sm"
                                    type="text"
                                    name="herFirstName"
                                    id="herFirstName"
                                    placeholder={`${t("her_first_name")}`}
                                    value={data.herFirstName}
                                    onChange={(e) => handleChanges(e)}
                                    className="her-info-border"
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    FirstName is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.herLastName
                                        ? null
                                        : "highlightField1"
                                    }
                                  >
                                    {" "}
                                    {t("her_last_name")}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Control
                                    // required
                                    size="sm"
                                    type="text"
                                    name="herLastName"
                                    id="herLastName"
                                    placeholder={`${t("her_last_name")}`}
                                    value={data.herLastName}
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    LastName is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.herEmail ? null : "highlightField1"
                                    }
                                  >
                                    {t("her_email")}{" "}
                                    <span className="text-danger">*</span>
                                  </Form.Label>
                                  <CopyableInput
                                    // required
                                    size="sm"
                                    type="text"
                                    name="herEmail"
                                    id="herEmail"
                                    placeholder={`${t("her_email")}`}
                                    value={data.herEmail}
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  {emailErrors.herEmail && (
                                    <span
                                      className="text-danger"
                                      style={{ fontSize: "12px" }}
                                    >
                                      {emailErrors.herEmail}
                                    </span>
                                  )}
                                  <EmailDeliverabilityStatus status={herEmailStatus} />
                                  <Form.Control.Feedback type="invalid">
                                    Email is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col lg={12} md={12} xs={12}>
                                <Form.Group className="form-group">
                                  <Form.Label
                                    className={
                                      data.herMobile ? null : "highlightField1"
                                    }
                                  >
                                    {t("her_mobile")}
                                  </Form.Label>
                                  <PhoneInput
                                    name="herMobile"
                                    value={data.herMobile}
                                    onChange={(e) => handleChanges(e)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Mobile is required!
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </div>
                          </Col>
                        </Row>
                        <div id="section-1"></div>
                        <hr></hr>
                        <div className="rr-section-card mt-4 mb-4">
                          <h4 className="rr-section-title">
                            <i className="mdi mdi-map-marker"></i> Address & Parish Information
                          </h4>
                          <div className="row locationFields">
                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={
                                  data.address ? null : "highlightField1"
                                }
                              >
                                {t("address")}
                              </Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="address"
                                id="address"
                                placeholder={`${t("address")}`}
                                value={data.address}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                Address is required!
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={data.state ? null : "highlightField1"}
                              >
                                {t("state")}
                              </Form.Label>
                              <FormSelect
                                className="minimal locationDropdown"
                                size="sm"
                                name="state"
                                id="state"
                                placeholder={`${t("state")}`}
                                value={data.state}
                                onChange={(e) => handleChanges(e)}
                              >
                                <option value="">{t("select_state")}</option>
                                <option value="Alaska">Alaska</option>
                                <option value="Alabama">Alabama</option>
                                <option value="Arkansas">Arkansas</option>
                                <option value="Arizona">Arizona</option>
                                <option value="California">California</option>
                                <option value="Colorado">Colorado</option>
                                <option value="Connecticut">Connecticut</option>
                                <option value="District of Columbia">
                                  District of Columbia
                                </option>
                                <option value="Delaware">Delaware</option>
                                <option value="Florida">Florida</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Hawaii">Hawaii</option>
                                <option value="Iowa">Iowa</option>
                                <option value="Idaho">Idaho</option>
                                <option value="Illinois">Illinois</option>
                                <option value="Indiana">Indiana</option>
                                <option value="Kansas">Kansas</option>
                                <option value="Kentucky">Kentucky</option>
                                <option value="Louisiana">Louisiana</option>
                                <option value="Massachusetts">
                                  Massachusetts
                                </option>
                                <option value="Maryland">Maryland</option>
                                <option value="Maine">Maine</option>
                                <option value="Michigan">Michigan</option>
                                <option value="Minnesota">Minnesota</option>
                                <option value="Missouri">Missouri</option>
                                <option value="Mississippi">Mississippi</option>
                                <option value="Montana">Montana</option>
                                <option value="North Carolina<">
                                  North Carolina
                                </option>
                                <option value="North Dakota">North Dakota</option>
                                <option value="Nebraska">Nebraska</option>
                                <option value="New Hampshire">
                                  New Hampshire
                                </option>
                                <option value="New Jersey">New Jersey</option>
                                <option value="New Mexico">New Mexico</option>
                                <option value="Nevada">Nevada</option>
                                <option value="New York">New York</option>
                                <option value="Ohio">Ohio</option>
                                <option value="Oklahoma">Oklahoma</option>
                                <option value="Oregon">Oregon</option>
                                <option value="Pennsylvania">Pennsylvania</option>
                                <option value="Puerto Rico">Puerto Rico</option>
                                <option value="Rhode Island">Rhode Island</option>
                                <option value="South Carolina">
                                  South Carolina
                                </option>
                                <option value="South Dakota">South Dakota</option>
                                <option value="Tennessee">Tennessee</option>
                                <option value="Texas">Texas</option>
                                <option value="Utah">Utah</option>
                                <option value="Virginia">Virginia</option>
                                <option value="Vermont">Vermont</option>
                                <option value="Washington">Washington</option>
                                <option value="Wisconsin">Wisconsin</option>
                                <option value="West Virginia">
                                  West Virginia
                                </option>
                                <option value="Wyoming">Wyoming</option>
                              </FormSelect>
                              <Form.Control.Feedback type="invalid">
                                State is required!
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={data.city ? null : "highlightField1"}
                              >
                                {t("city")}
                              </Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="city"
                                id="city"
                                placeholder={`${t("city")}`}
                                value={data.city}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                City is required!
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={data.zip ? null : "highlightField1"}
                              >
                                {t("zip")}
                              </Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="zip"
                                id="zip"
                                placeholder={`${t("zip")}`}
                                value={data.zip}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                Zip is required!
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={`${data.parishId ? "" : "highlightField1"
                                  }`}
                              >
                                {t("parish")}{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Select
                                required
                                name="parish"
                                options={parishList}
                                value={selectedParish}
                                onChange={handleSelectParish}
                                labelledBy="Select"
                                placeholder={`${t("select_parish")}`}
                                isSearchable={true}
                                isMulti={false}
                              />
                              <span>{data.parishName}</span>
                              <Form.Control.Feedback type="invalid">
                                ${t("parish_required")}
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={`label-inline ${data.anniversary_date ? "" : "highlightField1"
                                  }`}
                              >
                                {t("anniversary_date")}{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <DatePicker
                                required
                                autoComplete="off"
                                dateFormat="MM-dd-yyyy"
                                placeholderText="mm-dd-yyyy"
                                selected={data?.anniversary_date}
                                onChange={(date) => handleDatePicker(date)}
                                className="form-select setdate datepicker"
                                name="anniversary_date"
                                id="anniversary_date"
                                maxDate={new Date()}
                                showDisabledMonthNavigation
                                renderCustomHeader={({
                                  date,
                                  changeYear,
                                  changeMonth,
                                  decreaseMonth,
                                  increaseMonth,
                                  prevMonthButtonDisabled,
                                  nextMonthButtonDisabled,
                                }) => {
                                  const years = Array.from(
                                    { length: 100 },
                                    (_, i) => new Date().getFullYear() - i,
                                  );
                                  const months = [
                                    "January",
                                    "February",
                                    "March",
                                    "April",
                                    "May",
                                    "June",
                                    "July",
                                    "August",
                                    "September",
                                    "October",
                                    "November",
                                    "December",
                                  ];

                                  return (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        paddingBottom: "8px",
                                      }}
                                    >
                                      {/* ✅ BIGGER Month + Year dropdowns ABOVE */}
                                      <div
                                        style={{
                                          marginBottom: "10px",
                                          display: "flex",
                                          gap: "10px",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {/* Month Dropdown */}
                                        <select
                                          style={{
                                            fontSize: "16px",
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                          }}
                                          value={months[date.getMonth()]}
                                          onChange={({ target: { value } }) =>
                                            changeMonth(months.indexOf(value))
                                          }
                                        >
                                          {months.map((month) => (
                                            <option key={month} value={month}>
                                              {month}
                                            </option>
                                          ))}
                                        </select>

                                        {/* Year Dropdown */}
                                        <select
                                          style={{
                                            fontSize: "16px",
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                          }}
                                          value={date.getFullYear()}
                                          onChange={({ target: { value } }) =>
                                            changeYear(Number(value))
                                          }
                                        >
                                          {years.map((year) => (
                                            <option key={year} value={year}>
                                              {year}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      {/* ✅ Optional month navigation below */}
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <button
                                          onClick={decreaseMonth}
                                          disabled={prevMonthButtonDisabled}
                                          style={{
                                            fontSize: "18px",
                                            padding: "2px 8px",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {"<"}
                                        </button>

                                        <span
                                          style={{
                                            fontWeight: "bold",
                                            fontSize: "16px",
                                          }}
                                        >
                                          {months[date.getMonth()]}{" "}
                                          {date.getFullYear()}
                                        </span>

                                        <button
                                          onClick={increaseMonth}
                                          disabled={nextMonthButtonDisabled}
                                          style={{
                                            fontSize: "18px",
                                            padding: "2px 8px",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {">"}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {t("anniversary_date_required")}
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label>{t("notes")}</Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="notes"
                                id="notes"
                                placeholder={`${t("notes")}`}
                                value={data.notes}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                Notes is required!
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-6 col-lg-6">
                              <Form.Label
                                className={`${data.allergies ? "" : "highlightField1"
                                  }`}
                              >
                                {t("dietary_allergies")}{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                required
                                size="sm"
                                type="text"
                                name="allergies"
                                id="allergies"
                                value={data.allergies}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                {t("dietary_allergies_required")}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </div>

                        <div className="rr-section-card mb-4">
                          <h4 className="rr-section-title">
                            <i className="mdi mdi-phone-alert"></i> {t("one_emergency_contact")}
                          </h4>
                          <div className="row locationFields">
                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label
                                className={`${data.emergency_name1 ? "" : "highlightField1"
                                  }`}
                              >
                                {t("emergency_name_1")} <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="emergency_name1"
                                id="emergency_name1"
                                placeholder={`${t("emergency_name_1")}`}
                                value={data.emergency_name1}
                                onChange={(e) => handleChanges(e)}
                              />
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label
                                className={`${data.emergency_relationship1
                                    ? ""
                                    : "highlightField1"
                                  }`}
                              >
                                {t("emergency_relationship_1")}{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="emergency_relationship1"
                                id="emergency_relationship1"
                                placeholder={`${t("emergency_relationship_1")}`}
                                value={data.emergency_relationship1}
                                onChange={(e) => handleChanges(e)}
                              />
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label
                                className={`${data.emergency_phone1 ? "" : "highlightField1"
                                  }`}
                              >
                                {t("emergency_phone_1")}{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <PhoneInput
                                name="emergency_phone1"
                                value={data.emergency_phone1}
                                onChange={(e) => handleChanges(e)}
                              />
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label>{t("emergency_name_2")}</Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="emergency_name2"
                                id="emergency_name2"
                                placeholder={`${t("emergency_name_2")}`}
                                value={data.emergency_name2}
                                onChange={(e) => handleChanges(e)}
                              />
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label>{t("emergency_relationship_2")}</Form.Label>
                              <Form.Control
                                // required
                                size="sm"
                                type="text"
                                name="emergency_relationship2"
                                id="emergency_relationship2"
                                placeholder={`${t("emergency_relationship_2")}`}
                                value={data.emergency_relationship2}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                Relationship is required!
                              </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="form-group col-xs-12 col-sm-12 col-md-4 col-lg-4">
                              <Form.Label>{t("emergency_phone_2")}</Form.Label>
                              <PhoneInput
                                name="emergency_phone2"
                                value={data.emergency_phone2}
                                onChange={(e) => handleChanges(e)}
                              />
                              <Form.Control.Feedback type="invalid">
                                Phone is required!
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </div>

                        <div className="rr-section-card mb-4">
                          <h4 className="rr-section-title">
                            <i className="mdi mdi-bullhorn"></i> {t("hear_us")}
                          </h4>
                          <div className="rr-checkbox-grid">
                            <label className="rr-checkbox-item">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel m-0"
                                name="Website"
                                value="Website"
                                onChange={handleCheckboxChange}
                                checked={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes("Website")
                                }
                                disabled={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    data.hear_us === "Website",
                                  )
                                }
                              />
                              {t("website")}
                            </label>

                            <label className="rr-checkbox-item">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel m-0"
                                name="Church Bulletin"
                                value="Church Bulletin"
                                onChange={handleCheckboxChange}
                                checked={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes("Church Bulletin")
                                }
                                disabled={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    data.hear_us === "Church Bulletin",
                                  )
                                }
                              />
                              {t("church_bulletin")}
                            </label>

                            <label className="rr-checkbox-item">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel m-0"
                                name="Facebook"
                                value="Facebook"
                                onChange={handleCheckboxChange}
                                checked={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes("Facebook")
                                }
                                disabled={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    data.hear_us === "Facebook",
                                  )
                                }
                              />
                              {t("facebook")}
                            </label>

                            <label className="rr-checkbox-item">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel m-0"
                                name="School Marquee"
                                value="School Marquee"
                                onChange={handleCheckboxChange}
                                checked={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes("School Marquee")
                                }
                                disabled={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    data.hear_us === "School Marquee",
                                  )
                                }
                              />
                              {t("school_marquee")}
                            </label>

                            <label className="rr-checkbox-item">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel m-0"
                                name="Pulpit Talk/Table in Church"
                                value="Pulpit Talk/Table in Church"
                                onChange={handleCheckboxChange}
                                checked={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    "Pulpit Talk/Table in Church",
                                  )
                                }
                                disabled={
                                  data &&
                                  data.hear_us &&
                                  data.hear_us.includes(
                                    data.hear_us ===
                                    "Pulpit Talk/Table in Church",
                                  )
                                }
                              />
                              {t("Pulpit_church")}
                            </label>
                          </div>

                          <Row className="mt-4">
                            <Col lg={6} md={6} xs={12} sm={12}>
                              <Form.Group className="form-group">
                                <Form.Label className="bold">{t("referral")}</Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  name="referral_from_a_friend_or_family_member"
                                  placeholder={`${t("referral")}`}
                                  id="referral_from_a_friend_or_family_member"
                                  value={
                                    data.referral_from_a_friend_or_family_member
                                  }
                                  className="form-control"
                                  onChange={(e) => {
                                    handleChanges(e);
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>

                        <div className="rr-release-card">
                          <h4 className="rr-release-title">
                            {t("release_statement_title")}{" "}
                            <span className="text-danger">*</span>
                          </h4>

                          <p className="rr-release-text">
                            {t("release_statement")}
                          </p>

                          <div className="rr-acknowledgment-grid">
                            <label className="form-check-label rr-checkbox-item mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel acknowledgmentCheck m-0"
                                name="husband_acknowledgment"
                                value="husband_acknowledgment"
                                onChange={acknowledgmentCheckbox}
                                checked={
                                  acknowledgments.husband_acknowledgment
                                }
                              />
                              {t("husband_acknowledgment")}
                            </label>

                            <label className="form-check-label rr-checkbox-item mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input ageLabel acknowledgmentCheck m-0"
                                name="wife_acknowledgment"
                                value="wife_acknowledgment"
                                onChange={acknowledgmentCheckbox}
                                checked={acknowledgments.wife_acknowledgment}
                              />
                              {t("wife_acknowledgment")}
                            </label>
                          </div>

                          {acknowledgmentAlert && (
                            <div className="mt-2 text-danger font-weight-bold">
                              {t("acknowledgmentAlertMsg")}
                            </div>
                          )}
                        </div>

                        <Col
                          lg={12}
                          md={12}
                          xs={12}
                          className="text-center mt-5 mb-3"
                        >
                          <Button variant="primary" type="submit" className="rr-btn-submit">
                            <i className="mdi mdi-heart-outline" style={{ fontSize: "1.2rem" }}></i>
                            <span>{t("register")}</span>
                          </Button>
                        </Col>
                      </Form>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="content-wrapper align-items-center auth px-0">
              <div className="row w-100 mx-0">
                <div className="col-lg-10 mx-auto">
                  <div className="auth-form-light text-center main-register py-5 px-4 px-sm-5">
                    <h3>No Retreat</h3>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {show ? (
          <AlertModal
            show={show}
            handleClose={handleClose}
            message={`
<p> 
  <strong>${t("his_name")}</strong> ${!data.hisFirstName || data.hisFirstName === "NILL"
                ? "NIL"
                : `${data.hisFirstName} ${data.hisLastName || ""}`
              }<br/>
  <strong> ${t("his_email")}</strong> ${!data.hisEmail || data.hisEmail === "NILL" ? "NIL" : data.hisEmail
              }<br/><br/>
  <strong>${t("her_name")}</strong> ${!data.herFirstName || data.herFirstName === "NILL"
                ? "NIL"
                : `${data.herFirstName} ${data.herLastName || ""}`
              }<br/>
  <strong>${t("her_email")}</strong> ${!data.herEmail || data.herEmail === "NILL" ? "NIL" : data.herEmail
              }
</p>`}
            actionFunction={coupleMatched}
            cancel={t("cancel_button")}
            okay={t("okay_button")}
            heading={t("alert_msg")}
          />
        ) : null}
        {showInfo ? (
          <AlertDelete
            show={showInfo}
            alertHeader={t("register_retreat")}
            alerMessage={
              <div>
                <h5>{matched ? t("matched_mail") : t("not_matched_mail")}</h5>
              </div>
            }
            handleClose={handleCloseInfoAlert}
            buttonText={t("okay_alert_msg")}
          />
        ) : null}
        <OtherParishModal
          show={otherParishModalOpen}
          handleClose={() => setOtherParishModalOpen(false)}
          registerationData={data}
          setRegisterationData={setData}
          setParishList={setParishList}
          parishList={parishList}
        />
      </div>
    </>
  );
};
